<div class="container-fixed">
  <!-- Header -->
  <div class="flex flex-wrap items-center lg:items-end justify-between gap-5 pb-7.5">
    <div class="flex flex-col justify-center gap-2">
      <h1 class="text-xl font-semibold leading-none text-gray-900">
        Timecard Management
      </h1>
      <div class="flex items-center gap-2 text-sm font-medium text-gray-600">
        {{ getDisplayDate() }}
      </div>
    </div>
    <div class="flex items-center gap-2.5">
      <button class="btn btn-light btn-sm" (click)="goToToday()">
        Today
      </button>
      <button class="btn btn-icon btn-light btn-sm" (click)="goToPreviousDay()">
        <i class="ki-outline ki-left"></i>
      </button>
      <button class="btn btn-icon btn-light btn-sm" (click)="goToNextDay()">
        <i class="ki-outline ki-right"></i>
      </button>
    </div>
  </div>

  <!-- Filters -->
  <div class="card mb-5">
    <div class="card-body">
      <div class="flex flex-wrap gap-4 items-end">
        <!-- Date Picker -->
        <div class="flex flex-col gap-1">
          <label class="form-label text-gray-900">Date</label>
          <input
            type="date"
            class="input input-sm w-48"
            [(ngModel)]="selectedDate"
            (change)="onDateChange()">
        </div>

        <!-- Branch Filter -->
        <div class="flex flex-col gap-1" *ngIf="branches.length > 1">
          <label class="form-label text-gray-900">Branch</label>
          <select
            class="select select-sm w-48"
            [(ngModel)]="selectedBranchId"
            (change)="onBranchChange()">
            <option [ngValue]="null">All Branches</option>
            <option *ngFor="let branch of branches" [ngValue]="branch.branchID">
              {{ branch.branchName }}
            </option>
          </select>
        </div>

        <!-- Refresh Button -->
        <button class="btn btn-sm btn-light" (click)="loadDailyView()">
          <i class="ki-outline ki-arrows-circle"></i>
          Refresh
        </button>

        <!-- Spacer -->
        <div class="flex-grow"></div>

        <!-- Generate PDF Report Button -->
        <button class="btn btn-sm btn-primary" (click)="openReportModal()">
          <i class="ki-outline ki-document"></i>
          Generate PDF Report
        </button>
      </div>
    </div>
  </div>

  <!-- Main Content -->
  <div class="card">
    <div class="card-header">
      <h3 class="card-title">Staff Timecards</h3>
    </div>
    <div class="card-body">
      <!-- Loading State -->
      <div *ngIf="isLoading" class="flex justify-center py-10">
        <div class="spinner-border text-primary" role="status">
          <span class="sr-only">Loading...</span>
        </div>
      </div>

      <!-- No Data State -->
      <div *ngIf="!isLoading && dailyViewData.length === 0" class="text-center py-10 text-gray-500">
        <i class="ki-outline ki-calendar text-4xl mb-3"></i>
        <p>No staff schedules or timecards found for this date.</p>
        <p class="text-sm">Staff will appear here when they have schedules or clock in.</p>
      </div>

      <!-- Data Table -->
      <div *ngIf="!isLoading && dailyViewData.length > 0" class="scrollable-x-auto">
        <table class="table table-auto table-border">
          <thead>
            <tr>
              <th class="min-w-[180px]">Staff</th>
              <th class="min-w-[140px]">Scheduled</th>
              <th class="min-w-[180px]">Time In - Time Out</th>
              <th class="min-w-[80px]">Hours</th>
              <th class="min-w-[100px]">Status</th>
              <th class="min-w-[180px] text-center">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let staff of dailyViewData">
              <!-- Staff Name -->
              <td>
                <div class="flex items-center gap-2.5">
                  <div class="flex items-center justify-center w-9 h-9 rounded-full bg-gray-100 text-gray-700 font-semibold">
                    {{ staff.firstName?.charAt(0) }}{{ staff.lastName?.charAt(0) }}
                  </div>
                  <div class="flex flex-col gap-0.5">
                    <span class="text-sm font-medium text-gray-900">{{ staff.staffName }}</span>
                    <span class="text-xs text-gray-600" *ngIf="staff.branchName">{{ staff.branchName }}</span>
                    <!-- Session indicator for multi clock-in/out -->
                    <span class="text-xs text-primary" *ngIf="getSessionLabel(staff)">{{ getSessionLabel(staff) }}</span>
                  </div>
                </div>
              </td>

              <!-- Scheduled Time -->
              <td>
                <span *ngIf="staff.scheduledStartTime && staff.scheduledEndTime">
                  {{ formatScheduledTime(staff.scheduledStartTime) }} - {{ formatScheduledTime(staff.scheduledEndTime) }}
                </span>
                <span *ngIf="!staff.scheduledStartTime && staff.clockInTime" class="badge badge-warning badge-sm">
                  Unscheduled
                </span>
                <span *ngIf="!staff.scheduledStartTime && !staff.clockInTime" class="text-gray-400">-</span>
              </td>

              <!-- Actual Time -->
              <td>
                <div class="flex items-center gap-1">
                  <span class="text-success" *ngIf="staff.clockInTime">
                    {{ formatActualTime(staff.clockInTime) }}
                  </span>
                  <span *ngIf="!staff.clockInTime" class="text-gray-400">-</span>
                  <span class="mx-1">-</span>
                  <span class="text-danger" *ngIf="staff.clockOutTime">
                    {{ formatActualTime(staff.clockOutTime) }}
                  </span>
                  <span *ngIf="!staff.clockOutTime && staff.clockInTime" class="text-gray-400">Working...</span>
                  <span *ngIf="!staff.clockOutTime && !staff.clockInTime" class="text-gray-400">-</span>
                </div>
              </td>

              <!-- Hours Worked -->
              <td>
                <div class="flex flex-col gap-0.5">
                  <span *ngIf="staff.totalHours !== null && staff.totalHours !== undefined">
                    {{ staff.totalHours | number:'1.2-2' }}h
                  </span>
                  <span *ngIf="staff.totalHours === null || staff.totalHours === undefined" class="text-gray-400">-</span>
                  <!-- Total day hours for multi-session -->
                  <span class="text-xs text-gray-500" *ngIf="getTotalDayHoursLabel(staff)">{{ getTotalDayHoursLabel(staff) }}</span>
                </div>
              </td>

              <!-- Status -->
              <td>
                <span [class]="getStatusClass(staff.clockStatus)">
                  {{ getStatusLabel(staff.clockStatus) }}
                </span>
              </td>

              <!-- Actions -->
              <td class="text-center">
                <div class="flex justify-center gap-1">
                  <!-- Clock In Button (enabled when not currently clocked in) -->
                  <button
                    class="btn btn-sm btn-success"
                    [disabled]="!canClockIn(staff)"
                    (click)="clockIn(staff)"
                    title="Clock In">
                    <i class="ki-outline ki-entrance-left"></i>
                    Clock-in
                  </button>

                  <!-- Clock Out Button (enabled when currently clocked in) -->
                  <button
                    class="btn btn-sm btn-secondary"
                    [disabled]="!canClockOut(staff)"
                    (click)="clockOut(staff)"
                    title="Clock Out">
                    <i class="ki-outline ki-exit-right"></i>
                    Clock-out
                  </button>

                  <!-- Add Session Button (for adding manual entries) -->
                  <button
                    class="btn btn-sm btn-icon btn-light"
                    (click)="openCreateTimecardModal(staff)"
                    title="Add Manual Session">
                    <i class="ki-outline ki-plus"></i>
                  </button>

                  <!-- Edit Button -->
                  <button
                    class="btn btn-sm btn-icon btn-light"
                    *ngIf="staff.timecardID"
                    (click)="openEditTimecardModal(staff)"
                    title="Edit Timecard">
                    <i class="ki-outline ki-notepad-edit"></i>
                  </button>

                  <!-- Delete Button -->
                  <button
                    class="btn btn-sm btn-icon btn-light text-danger"
                    *ngIf="staff.timecardID"
                    (click)="deleteTimecard(staff)"
                    title="Delete Timecard">
                    <i class="ki-outline ki-trash"></i>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</div>

<!-- Edit Timecard Modal -->
<div class="modal" [class.show]="showTimecardModal" [style.display]="showTimecardModal ? 'block' : 'none'">
  <div class="modal-content max-w-[500px] top-[15%]">
    <div class="modal-header">
      <h3 class="modal-title">
        {{ modalMode === 'create' ? 'Add Timecard Session' : 'Edit Timecard' }}
      </h3>
      <button class="btn btn-xs btn-icon btn-light" (click)="closeTimecardModal()">
        <i class="ki-outline ki-cross"></i>
      </button>
    </div>
    <div class="modal-body">
      <form [formGroup]="timecardForm">
        <!-- Staff Name (Edit mode only) -->
        <div class="mb-4" *ngIf="selectedStaff">
          <label class="form-label">Staff Member</label>
          <div class="input input-sm bg-gray-100">{{ selectedStaff.staffName }}</div>
        </div>

        <!-- Date -->
        <div class="mb-4">
          <label class="form-label">Date</label>
          <input type="date" class="input input-sm" formControlName="workDate" readonly>
        </div>

        <!-- Clock In Time -->
        <div class="mb-4">
          <label class="form-label">Clock In Time</label>
          <input type="time" class="input input-sm" formControlName="clockInTime">
        </div>

        <!-- Clock Out Time -->
        <div class="mb-4">
          <label class="form-label">Clock Out Time</label>
          <input type="time" class="input input-sm" formControlName="clockOutTime">
        </div>

        <!-- Schedule Override Section (for historical entries) -->
        <div class="mb-4 p-3 bg-gray-50 rounded" *ngIf="modalMode === 'create'">
          <label class="form-label text-gray-700 mb-2">
            <i class="ki-outline ki-information-2 me-1"></i>
            Schedule Override (Optional)
          </label>
          <p class="text-xs text-gray-500 mb-3">
            For historical entries, you can specify what the schedule was at that time.
            Leave blank to use the current schedule.
          </p>
          <div class="flex gap-3">
            <div class="flex-1">
              <label class="form-label text-xs">Scheduled Start</label>
              <input type="time" class="input input-sm" formControlName="scheduledStartTime">
            </div>
            <div class="flex-1">
              <label class="form-label text-xs">Scheduled End</label>
              <input type="time" class="input input-sm" formControlName="scheduledEndTime">
            </div>
          </div>
        </div>

        <!-- Notes -->
        <div class="mb-4">
          <label class="form-label">Notes</label>
          <textarea class="textarea textarea-sm" rows="3" formControlName="notes" placeholder="Optional notes..."></textarea>
        </div>
      </form>
    </div>
    <div class="modal-footer justify-end">
      <button class="btn btn-light btn-sm" (click)="closeTimecardModal()">Cancel</button>
      <button class="btn btn-primary btn-sm" (click)="saveTimecard()" [disabled]="isLoading">
        <span *ngIf="isLoading" class="spinner-border spinner-border-sm me-2"></span>
        Save
      </button>
    </div>
  </div>
</div>
<div class="modal-backdrop" *ngIf="showTimecardModal" (click)="closeTimecardModal()"></div>

<!-- PDF Report Modal -->
<div class="modal" [class.show]="showReportModal" [style.display]="showReportModal ? 'block' : 'none'">
  <div class="modal-content max-w-[500px] top-[15%]">
    <div class="modal-header">
      <h3 class="modal-title">
        <i class="ki-outline ki-document me-2"></i>
        Generate Timecard Report
      </h3>
      <button class="btn btn-xs btn-icon btn-light" (click)="closeReportModal()">
        <i class="ki-outline ki-cross"></i>
      </button>
    </div>
    <div class="modal-body">
      <!-- Date Range -->
      <div class="mb-4">
        <label class="form-label">Date Range</label>
        <div class="flex gap-3">
          <div class="flex-1">
            <label class="form-label text-xs text-gray-500">From</label>
            <input type="date" class="input input-sm" [(ngModel)]="reportFromDate">
          </div>
          <div class="flex-1">
            <label class="form-label text-xs text-gray-500">To</label>
            <input type="date" class="input input-sm" [(ngModel)]="reportToDate">
          </div>
        </div>
      </div>

      <!-- Staff Filter -->
      <div class="mb-4">
        <label class="form-label">Staff Member</label>
        <select class="select select-sm" [(ngModel)]="reportStaffFilter">
          <option value="all">All Staff</option>
          <option *ngFor="let staff of staffListForReport" [value]="staff.userID">
            {{ staff.staffName }}
          </option>
        </select>
        <p class="text-xs text-gray-500 mt-1">Select a specific staff member or generate report for all staff</p>
      </div>

      <!-- Report Preview Info -->
      <div class="p-3 bg-gray-50 rounded">
        <p class="text-sm text-gray-700 mb-2"><strong>Report will include:</strong></p>
        <ul class="text-xs text-gray-600 list-disc pl-4 space-y-1">
          <li>Summary: Total hours per staff member</li>
          <li>Detailed daily breakdown of clock-in/out times</li>
          <li>Scheduled vs actual hours comparison</li>
        </ul>
      </div>
    </div>
    <div class="modal-footer justify-end">
      <button class="btn btn-light btn-sm" (click)="closeReportModal()">Cancel</button>
      <button class="btn btn-primary btn-sm" (click)="generatePdfReport()" [disabled]="isGeneratingReport">
        <span *ngIf="isGeneratingReport" class="spinner-border spinner-border-sm me-2"></span>
        <i *ngIf="!isGeneratingReport" class="ki-outline ki-file-down me-1"></i>
        Generate PDF
      </button>
    </div>
  </div>
</div>
<div class="modal-backdrop" *ngIf="showReportModal" (click)="closeReportModal()"></div>
